/**
 * Zustand Auth Store - Quản lý trạng thái xác thực
 *
 * Chức năng:
 * - Lưu trữ token JWT, thông tin người dùng
 * - Quản lý trạng thái đăng nhập/đăng xuất
 * - Kiểm tra quyền (vai trò, menu permissions)
 * - Persist dữ liệu vào localStorage (tự động đồng bộ qua tabs)
 *
 * API được sử dụng:
 * - POST /api/auth/login - Đăng nhập
 * - POST /api/auth/logout - Đăng xuất
 * - GET /api/auth/me - Thông tin người dùng hiện tại
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api, { setAuthToken, setUnauthorizedHandler } from '../api';

export interface RoleDefinition {
  id: number;
  code: string;
  name: string;
  status?: string;
}

export interface AuthUser {
  id: number;
  username: string;
  fullName: string;
  role: string;
  email?: string;
  departmentId?: number;
  jobTitle?: string;
  avatarPath?: string;
}

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  roleDefinitions: RoleDefinition[];
  allowedMenuKeys: string[];
  authorizationLoading: boolean;
  authorizationLoaded: boolean;
  login: (token: string, user: AuthUser) => void;
  logout: () => void;
  isAuthenticated: () => boolean;
  hasRole: (roles: string[]) => boolean;
  hasMenuAccess: (menuKeys: string[]) => boolean;
  getRoleName: (roleCode?: string | null) => string;
  ensureAuthorizationLoaded: (force?: boolean) => Promise<void>;
}

let authorizationPromise: Promise<void> | null = null;

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      roleDefinitions: [],
      allowedMenuKeys: [],
      authorizationLoading: false,
      authorizationLoaded: false,

      login: (token, user) => {
        setAuthToken(token);
        set({
          token,
          user,
          roleDefinitions: [],
          allowedMenuKeys: [],
          authorizationLoading: false,
          authorizationLoaded: false,
        });
      },

      logout: () => {
        setAuthToken(null);
        authorizationPromise = null;
        set({
          token: null,
          user: null,
          roleDefinitions: [],
          allowedMenuKeys: [],
          authorizationLoading: false,
          authorizationLoaded: false,
        });
      },

      isAuthenticated: () => !!get().token,

      hasRole: (roles) => {
        const user = get().user;
        return !!user && roles.indexOf(user.role) !== -1;
      },

      hasMenuAccess: (menuKeys) => {
        if (!menuKeys.length) return true;
        const allowedMenuKeys = get().allowedMenuKeys;
        return menuKeys.some((menuKey) => allowedMenuKeys.includes(menuKey));
      },

      getRoleName: (roleCode) => {
        const normalizedRoleCode = String(roleCode || '').trim();
        if (!normalizedRoleCode) return 'Người dùng';

        const role = get().roleDefinitions.find((item) => item.code === normalizedRoleCode);
        return role?.name || normalizedRoleCode;
      },

      ensureAuthorizationLoaded: async (force = false) => {
        const state = get();
        if (!state.token) return;
        if (!force && state.authorizationLoaded) return;
        if (authorizationPromise) return authorizationPromise;

        set({ authorizationLoading: true });

        authorizationPromise = (async () => {
          try {
            const [roleResponse, permissionResponse] = await Promise.all([
              api.get('/admin/roles/catalog'),
              api.get('/admin/roles/user-menu'),
            ]);

            set({
              roleDefinitions: Array.isArray(roleResponse.data) ? roleResponse.data : [],
              allowedMenuKeys: Array.isArray(permissionResponse.data) ? permissionResponse.data : [],
              authorizationLoaded: true,
              authorizationLoading: false,
            });
          } catch {
            set({
              roleDefinitions: [],
              allowedMenuKeys: [],
              authorizationLoaded: true,
              authorizationLoading: false,
            });
          } finally {
            authorizationPromise = null;
          }
        })();

        return authorizationPromise;
      },
    }),
    {
      name: 'cms-auth',
      partialize: (state) => ({
        token: state.token,
        user: state.user,
      }),
      onRehydrateStorage: () => (state) => {
        // Re-attach token to axios on hydration
        if (state?.token) setAuthToken(state.token);
      },
    }
  )
);

setUnauthorizedHandler(() => {
  useAuthStore.getState().logout();
});
